import { Component, inject, signal } from '@angular/core';
import { arabicNameValidators, optionalName } from '../../../../validators/name-rules';
import { CompaniesService } from '../../../../services/companies/companies.service';
import { Router } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CompanyRequestType } from '../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { CompaniesFileMgt } from '../../../../services/companies/filemgt.companies.service';

@Component({
  selector: 'app-add-company-form',
  imports: [ReactiveFormsModule],
  templateUrl: './add-company-form.html',
  styleUrl: './add-company-form.scss',
})
export class AddCompanyForm {
  private companyService = inject(CompaniesService);
  private companyFileMgt = inject(CompaniesFileMgt);
  private router = inject(Router);

  loading = signal<boolean>(false);
  errorCreatingCompany = signal<string | null>(null);

  selectedImage: File | null = null;
  imagePreview: string | null = null;
  formSubmitted = false;
  isDragging = false;

  companyForm = new FormGroup({
    titleEn: new FormControl('', [Validators.minLength(2), Validators.maxLength(100)]),
    titleAr: new FormControl('', arabicNameValidators(100)),
    descriptionEn: new FormControl('', [Validators.maxLength(4000)]),
    descriptionAr: new FormControl('', [Validators.maxLength(4000)]),
    isActive: new FormControl(false, {
      validators: [Validators.required],
    }),
    profileImageFid: new FormControl(''),
  });

  ngOnInit() {}

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    this.processFile(file);

    input.value = '';
  }

  processFile(file: File) {
    if (!file.type.match(/image\/(png|jpe?g)/)) {
      alert($localize`:@@companyFormFileTypeError:Only PNG and JPG files are allowed`);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert($localize`:@@companyFormFileSizeError:File ${file.name} exceeds 5MB limit`);
      return;
    }

    this.selectedImage = file;

    const reader = new FileReader();
    reader.onload = (e) => {
      this.imagePreview = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  removeImage() {
    this.selectedImage = null;
    this.imagePreview = null;
  }

  onSubmit() {
    this.formSubmitted = true;

    if (!this.selectedImage) {
      this.errorCreatingCompany.set('Please upload a category logo');
      return;
    }

    if (this.companyForm.invalid) {
      for (const key in this.companyForm.controls) {
        this.companyForm.get(key)?.markAsTouched();
      }
      return;
    }

    this.loading.set(true);
    this.errorCreatingCompany.set(null);

    this.companyFileMgt.uploadImage(this.selectedImage).subscribe({
      next: (result) => {
        console.log('image uploaded: ', result);
        const formValue = this.companyForm.getRawValue();
        const payload: CompanyRequestType = {
          titleEn: optionalName(formValue.titleEn),
          titleAr: formValue.titleAr!.trim(),
          descriptionEn: formValue.descriptionEn!,
          descriptionAr: formValue.descriptionAr!,
          // isActive: formValue.isActive!,
          profileImageFid: result.fid,
        };

        this.companyService.createMFGCompany(payload).subscribe({
          next: () => {
            this.loading.set(false);
            this.router.navigate(['/admin/companies']);
          },
          error: (err) => {
            console.error('Error creating company:', err);
            this.loading.set(false);
            this.errorCreatingCompany.set('Failed to create company. Please try again.');
          },
        });
      },
      error: (error) => {
        console.error('Error uploading image:', error);
        this.loading.set(false);
        this.errorCreatingCompany.set('Failed to upload image. Please try again.');
      },
    });
  }

  onCancel() {
    this.router.navigate(['/admin/companies']);
  }
}
